import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Trophy, Bike, Footprints, Waves, Plus, Trash2, Eye, CheckCircle2 } from 'lucide-react';

const getSportDetails = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'running' || t.includes('run') || t.includes('carrer')) {
    return { Icon: Footprints, label: 'Carrera', color: '#f59e0b', border: '#f59e0b' };
  }
  if (t === 'cycling' || t.includes('ride') || t.includes('bici')) {
    return { Icon: Bike, label: 'Ciclismo', color: '#fc4c02', border: '#fc4c02' };
  }
  if (t === 'swimming' || t.includes('swim') || t.includes('natac')) {
    return { Icon: Waves, label: 'Natación', color: '#06b6d4', border: '#06b6d4' };
  }
  if (t === 'triathlon' || t.includes('triat')) {
    return { Icon: Trophy, label: 'Triatlón', color: '#8b5cf6', border: '#8b5cf6' };
  }
  return { Icon: Trophy, label: 'Competición', color: '#fcd34d', border: '#fcd34d' };
};

const formatDistance = (val) => {
  if (!val && val !== 0) return 'N/A';
  const s = String(val).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return `${s} km`;
  return s;
};

const formatElevation = (val) => {
  if (!val && val !== 0) return 'N/A';
  const s = String(val).trim();
  if (/^\d+$/.test(s)) return `+${s} m`;
  return s;
};

const formatTargetTime = (val) => {
  if (!val && val !== 0) return 'N/A';
  const s = String(val).trim();
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    return n < 12 ? `${n}h` : `${n} min`;
  }
  return s;
};

const getPeriodizationPhase = (days) => {
  if (days <= 0) {
    return {
      name: 'Día de Competición / Reto',
      desc: 'Activación mínima o ejecución del evento',
      color: '#10b981',
      badgeBg: 'rgba(16, 185, 129, 0.15)',
      border: 'rgba(16, 185, 129, 0.4)'
    };
  }
  if (days <= 7) {
    return {
      name: 'Puesta a punto (Tapering)',
      desc: 'Reducción de volumen y máxima frescura',
      color: '#00e5ff',
      badgeBg: 'rgba(0, 229, 255, 0.15)',
      border: 'rgba(0, 229, 255, 0.4)'
    };
  }
  if (days <= 21) {
    return {
      name: 'Afinado y Ritmo Específico',
      desc: 'Intervalos al ritmo y vatios objetivo',
      color: '#f59e0b',
      badgeBg: 'rgba(245, 158, 11, 0.15)',
      border: 'rgba(245, 158, 11, 0.4)'
    };
  }
  if (days <= 60) {
    return {
      name: 'Fase Específica de Rendimiento',
      desc: 'Bloques clave dirigidos a las demandas del reto',
      color: '#10b981',
      badgeBg: 'rgba(16, 185, 129, 0.15)',
      border: 'rgba(16, 185, 129, 0.4)'
    };
  }
  return {
    name: 'Fase de Base y Volumen',
    desc: 'Construcción aeróbica y trabajo de fuerza',
    color: '#818cf8',
    badgeBg: 'rgba(129, 140, 248, 0.15)',
    border: 'rgba(129, 140, 248, 0.4)'
  };
};

const UpcomingEvents = ({ events = [], onAddEvent, onDeleteEvent }) => {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Ordenar cronológicamente todos los eventos
  const sortedEvents = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Evento activo para la cuenta atrás y vista destacada (por defecto el primer Objetivo A o el más próximo)
  const defaultMainEvent = sortedEvents.find(e => e.priority === 'A') || sortedEvents[0];
  const activeEvent = (selectedEventId ? sortedEvents.find(e => e.id === selectedEventId) : null) || defaultMainEvent;

  useEffect(() => {
    if (!activeEvent) return;

    const calculateTimeLeft = () => {
      const difference = +new Date(activeEvent.date) - +new Date();
      let timeLeftObj = { days: 0, hours: 0, minutes: 0, seconds: 0 };

      if (difference > 0) {
        timeLeftObj = {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        };
      }
      return timeLeftObj;
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [activeEvent]);

  if (!events || events.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Trophy size={20} color="#fcd34d" /> Próximos Retos
          </h3>
        </div>
        <div className="premium-glow-card" style={{ 
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
          padding: '4rem 2rem', textAlign: 'center', gap: '1rem', 
          background: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.75), rgba(2, 6, 23, 0.95)), url("https://images.unsplash.com/photo-1552674605-15c2145bc81a?q=80&w=1200&fm=jpg&auto=compress&fit=crop")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
        }}>
          <div style={{ background: 'rgba(252, 211, 77, 0.15)', padding: '1.5rem', borderRadius: '50%', marginBottom: '0.5rem', boxShadow: '0 0 30px rgba(252, 211, 77, 0.2)' }}>
            <Trophy size={48} color="#fcd34d" />
          </div>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#fff', fontWeight: '700' }}>No tienes competiciones a la vista</h3>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem', maxWidth: '480px', lineHeight: '1.5' }}>
              Añade tu próximo reto para mantener la motivación a tope. Puede ser una competición oficial o un test de rendimiento.
            </p>
          </div>
          <button onClick={onAddEvent} style={{
            marginTop: '1.5rem', background: '#fcd34d', color: '#000', border: 'none', padding: '0.85rem 2rem',
            borderRadius: '12px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.5rem',
            boxShadow: '0 4px 15px rgba(252, 211, 77, 0.3)'
          }} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}>
            <Plus size={20} /> Añadir Competición
          </button>
        </div>
      </div>
    );
  }

  const activeSport = getSportDetails(activeEvent?.type);
  const ActiveSportIcon = activeSport.Icon;
  const activePhase = getPeriodizationPhase(timeLeft.days);

  return (
    <div className="premium-glow-card" style={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '1.25rem', 
      marginBottom: '1.5rem',
      padding: '1.5rem',
      borderRadius: '16px',
      border: '1px solid rgba(255,255,255,0.05)',
      background: 'linear-gradient(to right, rgba(15, 23, 42, 0.95) 30%, rgba(15, 23, 42, 0.4)), url("https://images.unsplash.com/photo-1552674605-15c2145bc81a?q=80&w=1200&fm=jpg&auto=compress&fit=crop")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
    }}>
      
      {/* HEADER DE SECCIÓN */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Trophy size={20} color="#fcd34d" /> Próximos Retos
          {sortedEvents.length > 0 && (
            <span style={{ fontSize: '0.75rem', background: 'rgba(252, 211, 77, 0.15)', color: '#fcd34d', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>
              {sortedEvents.length} activo{sortedEvents.length > 1 ? 's' : ''}
            </span>
          )}
        </h3>
        <button onClick={onAddEvent} style={{
          background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', padding: '0.45rem 0.85rem',
          borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px'
        }} onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }} onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}>
          <Plus size={14} /> Añadir Reto
        </button>
      </div>

      {/* SELECTOR DE RETOS RÁPIDO (SI HAY MÁS DE 1 RETO) */}
      {sortedEvents.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', overflowX: 'auto', paddingBottom: '4px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
            Enfocar en cuenta atrás:
          </span>
          {sortedEvents.map(event => {
            const isSelected = event.id === activeEvent?.id;
            const diffDays = Math.max(0, Math.ceil((new Date(event.date) - new Date()) / (1000 * 60 * 60 * 24)));
            const sDetails = getSportDetails(event.type);
            const SIcon = sDetails.Icon;

            return (
              <button
                key={event.id}
                onClick={() => setSelectedEventId(event.id)}
                style={{
                  background: isSelected ? 'rgba(252, 211, 77, 0.15)' : 'rgba(0,0,0,0.3)',
                  border: isSelected ? '1px solid #fcd34d' : '1px solid rgba(255,255,255,0.08)',
                  color: isSelected ? '#fcd34d' : 'var(--text-secondary)',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.78rem',
                  fontWeight: isSelected ? 700 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                <SIcon size={13} color={isSelected ? '#fcd34d' : sDetails.color} />
                <span>{event.name}</span>
                <span style={{ 
                  fontSize: '0.68rem', 
                  background: isSelected ? 'rgba(252, 211, 77, 0.25)' : 'rgba(255,255,255,0.08)', 
                  padding: '1px 6px', 
                  borderRadius: '10px',
                  color: isSelected ? '#fff' : 'inherit'
                }}>
                  {diffDays}d
                </span>
                {isSelected && <CheckCircle2 size={12} color="#fcd34d" />}
              </button>
            );
          })}
        </div>
      )}

      {/* TARJETA PRINCIPAL DESTACADA: activeEvent */}
      {activeEvent && (
        <div style={{ 
          position: 'relative', 
          overflow: 'hidden',
          borderTop: `3px solid ${activeSport.border}`,
          background: 'rgba(0,0,0,0.25)',
          borderRadius: '14px',
          padding: '1.5rem',
          border: '1px solid rgba(255,255,255,0.06)'
        }}>
          {/* Fondo decorativo */}
          <div style={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: '320px',
            height: '320px',
            background: `radial-gradient(circle, ${activeSport.color}15 0%, transparent 70%)`,
            borderRadius: '50%',
            zIndex: 0,
            pointerEvents: 'none'
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              
              {/* Cabecera del Evento */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ 
                    background: 'rgba(245, 158, 11, 0.2)', 
                    color: '#fcd34d', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.65rem', 
                    fontWeight: 800,
                    letterSpacing: '0.5px'
                  }}>
                    OBJETIVO {activeEvent.priority || 'A'}
                  </span>
                  <span style={{ 
                    background: 'rgba(255,255,255,0.08)', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '0.65rem', 
                    fontWeight: 600
                  }}>
                    {activeSport.label}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapPin size={12} /> {activeEvent.location || 'Sin ubicación fijada'}
                  </span>
                </div>
                <h2 className="text-2xl" style={{ margin: 0, fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ActiveSportIcon size={26} color={activeSport.color} />
                  {activeEvent.name}
                </h2>
                <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Calendar size={14} /> {new Date(activeEvent.date).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>

              {/* Ficha Técnica */}
              <div style={{ 
                background: 'rgba(0,0,0,0.35)', 
                border: '1px solid rgba(255,255,255,0.08)', 
                borderRadius: '12px', 
                padding: '0.85rem 1.25rem',
                display: 'flex',
                gap: '1.5rem',
                flexWrap: 'wrap'
              }}>
                <div>
                  <span style={{ display: 'block', fontSize: '0.65rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Distancia</span>
                  <strong style={{ fontSize: '1.1rem', color: 'white' }}>{formatDistance(activeEvent.distance)}</strong>
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.65rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Desnivel</span>
                  <strong style={{ fontSize: '1.1rem', color: 'white' }}>{formatElevation(activeEvent.elevation)}</strong>
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.65rem', color: '#fcd34d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tiempo Objetivo</span>
                  <strong style={{ fontSize: '1.1rem', color: '#f59e0b' }}>{formatTargetTime(activeEvent.targetTime)}</strong>
                </div>
              </div>
            </div>

            {/* Cuenta Atrás Gigante + Estado Dinámico de Periodización */}
            <div style={{ marginTop: '1.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'white', lineHeight: 1, fontFamily: 'monospace' }}>
                    {String(timeLeft.days || 0).padStart(2, '0')}
                  </div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>Días</span>
                </div>
                <span style={{ fontSize: '2.2rem', color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>:</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'white', lineHeight: 1, fontFamily: 'monospace' }}>
                    {String(timeLeft.hours || 0).padStart(2, '0')}
                  </div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>Horas</span>
                </div>
                <span style={{ fontSize: '2.2rem', color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>:</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'white', lineHeight: 1, fontFamily: 'monospace' }}>
                    {String(timeLeft.minutes || 0).padStart(2, '0')}
                  </div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>Mins</span>
                </div>
                <span style={{ fontSize: '2.2rem', color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>:</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: '2.8rem', fontWeight: 900, color: activeSport.color, lineHeight: 1, fontFamily: 'monospace' }}>
                    {String(timeLeft.seconds || 0).padStart(2, '0')}
                  </div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>Segs</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
                <div style={{ 
                  background: activePhase.badgeBg, 
                  border: `1px solid ${activePhase.border}`, 
                  padding: '0.65rem 1rem', 
                  borderRadius: '12px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  maxWidth: '300px'
                }}>
                  <Trophy size={20} color={activePhase.color} style={{ flexShrink: 0 }} />
                  <div>
                    <span style={{ display: 'block', fontSize: '0.68rem', color: activePhase.color, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      ESTADO ACTUAL
                    </span>
                    <span style={{ fontSize: '0.85rem', color: 'white', fontWeight: 600 }}>
                      {activePhase.name}
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    if (window.confirm(`¿Estás seguro de que deseas eliminar el reto "${activeEvent.name}"?`)) {
                      onDeleteEvent(activeEvent.id);
                      if (selectedEventId === activeEvent.id) setSelectedEventId(null);
                    }
                  }} 
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '0.75rem', borderRadius: '12px', color: '#ef4444', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }} 
                  title="Eliminar reto"
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                  onMouseOut={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LISTA COMPLETA DE TODOS LOS RETOS EN PLANIFICACIÓN */}
      {sortedEvents.length > 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
              Todos tus retos en agenda ({sortedEvents.length})
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))', gap: '0.85rem' }}>
            {sortedEvents.map(event => {
              const isSelected = event.id === activeEvent?.id;
              const diffDays = Math.max(0, Math.ceil((new Date(event.date) - new Date()) / (1000 * 60 * 60 * 24)));
              const sDetails = getSportDetails(event.type);
              const SIcon = sDetails.Icon;
              const phaseInfo = getPeriodizationPhase(diffDays);

              return (
                <div 
                  key={event.id} 
                  className="technical-card" 
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '0.75rem', 
                    padding: '1.1rem',
                    borderRadius: '12px',
                    background: isSelected ? 'rgba(252, 211, 77, 0.05)' : 'rgba(0,0,0,0.3)',
                    border: isSelected ? '1px solid rgba(252, 211, 77, 0.4)' : '1px solid rgba(255,255,255,0.06)',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  {/* Fila Superior */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ 
                        background: 'rgba(255,255,255,0.06)', 
                        padding: '6px', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center' 
                      }}>
                        <SIcon size={16} color={sDetails.color} />
                      </div>
                      <div>
                        <h4 style={{ margin: 0, color: 'white', fontSize: '0.95rem', fontWeight: 700 }}>
                          {event.name}
                        </h4>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                          <MapPin size={10} /> {event.location || 'Sin ubicación'}
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ 
                        background: event.priority === 'A' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255,255,255,0.1)', 
                        color: event.priority === 'A' ? '#fcd34d' : 'var(--text-secondary)', 
                        padding: '2px 8px', 
                        borderRadius: '10px', 
                        fontSize: '0.65rem', 
                        fontWeight: 800 
                      }}>
                        OBJ {event.priority || 'A'}
                      </span>
                      <button 
                        onClick={() => {
                          if (window.confirm(`¿Eliminar reto "${event.name}"?`)) {
                            onDeleteEvent(event.id);
                            if (selectedEventId === event.id) setSelectedEventId(null);
                          }
                        }} 
                        style={{
                          background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px', borderRadius: '6px'
                        }} 
                        title="Eliminar reto"
                        onMouseOver={e => e.currentTarget.style.color = '#ef4444'}
                        onMouseOut={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Fila Ficha Técnica */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr 1fr', 
                    gap: '0.5rem', 
                    background: 'rgba(0,0,0,0.2)', 
                    padding: '0.5rem 0.75rem', 
                    borderRadius: '8px',
                    fontSize: '0.75rem'
                  }}>
                    <div>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.65rem', display: 'block', textTransform: 'uppercase' }}>Distancia</span>
                      <strong style={{ color: 'white' }}>{formatDistance(event.distance)}</strong>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.65rem', display: 'block', textTransform: 'uppercase' }}>Desnivel</span>
                      <strong style={{ color: 'white' }}>{formatElevation(event.elevation)}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#fcd34d', fontSize: '0.65rem', display: 'block', textTransform: 'uppercase' }}>Meta</span>
                      <strong style={{ color: '#f59e0b' }}>{formatTargetTime(event.targetTime)}</strong>
                    </div>
                  </div>

                  {/* Fila Inferior: Fecha + Countdown + Botón de Enfoque */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      <Calendar size={12} />
                      <span>{new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      <span style={{ 
                        background: phaseInfo.badgeBg, 
                        color: phaseInfo.color, 
                        padding: '1px 6px', 
                        borderRadius: '6px', 
                        fontSize: '0.65rem', 
                        fontWeight: 700 
                      }}>
                        {diffDays}d ({phaseInfo.name.split('(')[0].trim()})
                      </span>
                    </div>

                    {isSelected ? (
                      <span style={{ 
                        display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: '#fcd34d', fontWeight: 700 
                      }}>
                        <CheckCircle2 size={13} /> Enfocado arriba
                      </span>
                    ) : (
                      <button 
                        onClick={() => setSelectedEventId(event.id)} 
                        style={{
                          background: 'rgba(255,255,255,0.08)', 
                          border: '1px solid rgba(255,255,255,0.12)', 
                          color: 'white', 
                          padding: '0.3rem 0.6rem', 
                          borderRadius: '8px', 
                          fontSize: '0.72rem', 
                          fontWeight: 600, 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = 'rgba(252, 211, 77, 0.2)'; e.currentTarget.style.borderColor = '#fcd34d'; }}
                        onMouseOut={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                      >
                        <Eye size={12} /> Enfocar cuenta atrás
                      </button>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

export default UpcomingEvents;
